import { useState, useEffect, useRef, lazy, Suspense, type CSSProperties } from "react";
import { Link, useNavigate, useLocation } from "react-router";
import { Menu, X, Sun, Moon } from "lucide-react";
import { AttenzioButton } from "@/components/design-system";
import { useTheme } from "@/providers/theme";

const DemoRequestDialog = lazy(() =>
  import("@/components/marketing/DemoRequestDialog").then((m) => ({
    default: m.DemoRequestDialog,
  })),
);

type MegaItem = { label: string; to: string; desc: string };

const MEGA_PRODUCT: MegaItem[] = [
  { label: "Voice Agent", to: "/product#voice-agent", desc: "Answers every call in your name" },
  { label: "Bookings & scheduling", to: "/product#bookings", desc: "Straight into your calendar" },
  { label: "Missed-call rescue", to: "/product#missed-call-rescue", desc: "Voicemail becomes a booking" },
  { label: "Call log & transcripts", to: "/product#call-log", desc: "Every call, word for word" },
  { label: "Workflow automation", to: "/product#workflows", desc: "Call notes and next steps routed automatically" },
  { label: "Integrations", to: "/product#integrations", desc: "The systems you already run" },
];

const MEGA_INDUSTRIES: MegaItem[] = [
  { label: "Home Services", to: "/industries/home-services", desc: "Supervised HVAC intake and booking" },
  { label: "Hospitality", to: "/industries/hospitality", desc: "Reservations and overflow answering" },
  { label: "Legal & Insurance", to: "/industries/legal", desc: "Structured intake, every time" },
  { label: "Auto & Real Estate", to: "/industries/auto", desc: "Qualify leads while they're hot" },
  { label: "Property management", to: "/industries/property", desc: "Work orders filed on the first ring" },
];

const NAV_ITEMS: Array<{ label: string; to: string; mega?: MegaItem[] }> = [
  { label: "Product", to: "/product", mega: MEGA_PRODUCT },
  { label: "Industries", to: "/industries", mega: MEGA_INDUSTRIES },
  { label: "Customers", to: "/customers" },
  { label: "Pricing", to: "/pricing" },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mega, setMega] = useState<string | null>(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const megaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHome = location.pathname === "/";
  const overHero = onHome && !scrolled && !mobileOpen;
  const bg = overHero
    ? "transparent"
    : scrolled
      ? isDark
        ? "rgba(11,15,20,0.88)"
        : "rgba(255,255,255,0.9)"
      : isDark
        ? "rgba(11,15,20,0.72)"
        : "rgba(255,255,255,0.72)";
  const brandColor = overHero ? "var(--ink-50)" : "var(--text-strong)";
  const mutedIcon = overHero ? "var(--ink-300)" : "var(--text-muted)";
  // Over the dark hero, theme surface-hover is often light (esp. light mode) and
  // washes out the light nav labels — use a translucent chip instead.
  const navHoverBg = overHero ? "rgba(255,255,255,0.14)" : "var(--surface-hover)";
  const navHoverFg = overHero ? "var(--ink-50)" : "var(--text-strong)";

  const openMega = (label: string) => {
    if (megaTimer.current) clearTimeout(megaTimer.current);
    setMega(label);
  };
  const closeMega = () => {
    megaTimer.current = setTimeout(() => setMega(null), 150);
  };

  const activeMegaItems = NAV_ITEMS.find((item) => item.label === mega)?.mega;

  return (
    <header
      className={`${onHome ? "fixed inset-x-0 top-0" : "sticky top-0"} z-50`}
      style={{
        background: bg,
        backdropFilter: overHero ? "none" : "blur(14px)",
        borderBottom: overHero ? "1px solid transparent" : "1px solid var(--border-subtle)",
        transition: "background 0.2s ease, border-color 0.2s ease",
      }}
      onMouseLeave={closeMega}
    >
      <div className="max-w-[1160px] mx-auto px-6 sm:px-9 flex items-center h-[68px] gap-2">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" style={{ textDecoration: "none" }}>
          <img src={overHero ? "/mark-dark.svg" : "/mark.svg"} width="32" height="32" alt="Attenzio" />
          <span
            className="font-extrabold text-[19px] tracking-[-0.02em] whitespace-nowrap"
            style={{ fontFamily: "var(--font-display)", color: brandColor }}
          >
            Attenzio
          </span>
        </Link>
        <nav
          className="hidden lg:flex items-center ml-6"
          style={
            {
              ["--nav-hover-bg"]: navHoverBg,
              ["--nav-hover-fg"]: navHoverFg,
            } as CSSProperties
          }
        >
          {NAV_ITEMS.map((item) => {
            const isOpen = mega === item.label;
            const isActive = location.pathname === item.to || isOpen;
            const color = overHero
              ? isActive
                ? "var(--ink-50)"
                : "var(--ink-200)"
              : isActive
                ? "var(--brand)"
                : "var(--text-body)";
            return (
              <div key={item.label} onMouseEnter={() => item.mega && openMega(item.label)}>
                <Link
                  to={item.to}
                  className="site-header-nav-link font-semibold text-[15px] px-3 py-2.5 rounded-[var(--radius-md)] whitespace-nowrap border-none bg-transparent cursor-pointer transition-colors inline-block"
                  style={{
                    fontFamily: "var(--font-sans)",
                    color: isOpen ? navHoverFg : color,
                    textDecoration: "none",
                    background: isOpen ? navHoverBg : undefined,
                  }}
                >
                  {item.label}
                </Link>
              </div>
            );
          })}
        </nav>
        <div className="flex-1" />
        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
            style={{ color: mutedIcon }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = navHoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <AttenzioButton variant="ghost" onClick={() => navigate("/login")}>
            Sign in
          </AttenzioButton>
          <AttenzioButton variant="primary" onClick={() => setDemoOpen(true)}>
            Request a demo
          </AttenzioButton>
        </div>
        <button
          className="lg:hidden p-2 rounded-[var(--radius-md)]"
          onClick={() => setMobileOpen((p) => !p)}
          aria-label="Toggle menu"
          style={{ color: brandColor }}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {activeMegaItems && (
        <div
          className="hidden lg:block absolute left-0 right-0 border-b border-[var(--border-subtle)] shadow-lg"
          style={{ top: "68px", background: "var(--surface-card)", animation: "megain 0.2s cubic-bezier(0,0,0.2,1)" }}
          onMouseEnter={() => mega && openMega(mega)}
          onMouseLeave={closeMega}
        >
          <div className="max-w-[1160px] mx-auto px-6 sm:px-9">
            <div className="grid grid-cols-3 gap-2" style={{ padding: "40px 0 48px" }}>
              {activeMegaItems.map((m) => (
                <Link
                  key={m.label}
                  to={m.to}
                  onClick={() => setMega(null)}
                  className="block transition-colors hover:bg-[var(--surface-hover)]"
                  style={{ padding: "18px 20px", borderRadius: "var(--radius-lg)", textDecoration: "none" }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 700,
                      fontSize: "21px",
                      letterSpacing: "-0.01em",
                      color: "var(--text-strong)",
                    }}
                  >
                    {m.label}
                  </div>
                  <div className="mt-0.5" style={{ fontSize: "14px", color: "var(--text-muted)" }}>
                    {m.desc}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {mobileOpen && (
        <div className="lg:hidden border-t border-[var(--border-subtle)] px-6 sm:px-9 py-6" style={{ background: "var(--surface-card)" }}>
          <div className="flex flex-col gap-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="text-left px-3 py-2.5 rounded-[var(--radius-md)] font-semibold text-[15px] block"
                style={{
                  fontFamily: "var(--font-sans)",
                  color: isDark ? "var(--text-muted)" : "var(--ink-800)",
                  textDecoration: "none",
                }}
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-[var(--border-subtle)]">
              <button
                onClick={() => {
                  setMobileOpen(false);
                  toggleTheme();
                }}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-[var(--radius-md)] font-semibold text-[15px] transition-colors hover:bg-[var(--surface-hover)]"
                style={{ fontFamily: "var(--font-sans)", color: isDark ? "var(--text-muted)" : "var(--ink-800)" }}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {isDark ? "Light mode" : "Dark mode"}
              </button>
              <AttenzioButton
                variant="ghost"
                onClick={() => {
                  setMobileOpen(false);
                  navigate("/login");
                }}
              >
                Sign in
              </AttenzioButton>
              <AttenzioButton
                variant="primary"
                onClick={() => {
                  setMobileOpen(false);
                  setDemoOpen(true);
                }}
              >
                Request a demo
              </AttenzioButton>
            </div>
          </div>
        </div>
      )}

      <style>{"@keyframes megain{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}.site-header-nav-link:hover{background:var(--nav-hover-bg)!important;color:var(--nav-hover-fg)!important}"}</style>
      {demoOpen ? (
        <Suspense fallback={null}>
          <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
        </Suspense>
      ) : null}
    </header>
  );
}
